import { useCallback, useState } from "react";
import { ITeamMembersConveyanceList } from "../../../../Types/TeamMembers";

export const useConveyanceEditing = (
  rowKeyOf: (r: ITeamMembersConveyanceList) => any
) => {
  const [patches, setPatches] = useState<Record<string | number, number>>({});
  const [drafts, setDrafts] = useState<Record<string | number, string>>({});
  const [errors, setErrors] = useState<Record<string | number, string | null>>(
    {}
  );

  const validate = useCallback(
    (row: ITeamMembersConveyanceList, value: string): string | null => {
      if (value === "") return "Approved amount is required";
      const n = Number(value);
      if (!Number.isFinite(n)) return "Must be a number";
      if (n < 0) return "Cannot be negative";
      const claimed = Number(row.amount ?? 0);
      if (n > claimed) return `Cannot exceed claimed amount (${claimed})`;
      return null;
    },
    []
  );

  const setDraftAndValidate = useCallback(
    (row: ITeamMembersConveyanceList, value: string) => {
      const key = rowKeyOf(row);
      setDrafts((p) => ({ ...p, [key]: value }));
      const msg = validate(row, value);
      setErrors((p) => ({ ...p, [key]: msg }));
    },
    [rowKeyOf, validate]
  );

  const commitEdit = useCallback(
    (row: ITeamMembersConveyanceList, value: string) => {
      const key = rowKeyOf(row);
      const msg = validate(row, value);
      setErrors((p) => ({ ...p, [key]: msg }));
      if (msg) return;
      const n = Number(value);
      setPatches((p) => ({ ...p, [key]: n }));
      setDrafts((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    },
    [rowKeyOf, validate]
  );

  const getApprovedValue = useCallback(
    (row: ITeamMembersConveyanceList) => {
      const key = rowKeyOf(row);
      const patched = patches[key];
      if (patched !== undefined) return patched;
      const draft = drafts[key];
      if (draft !== undefined) return Number(draft);
      return Number(row.approved_amount ?? 0) || Number(row.amount ?? 0);
    },
    [patches, drafts, rowKeyOf]
  );

  return {
    patches,
    drafts,
    errors,
    setDraftAndValidate,
    commitEdit,
    getApprovedValue,
  };
};
